import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "../../../../hooks/useStore";
import User from "../../../../passapp-sdk/user";
import moment from "moment";
import { getCompanyNames } from "./getCompanyNames";

export interface UsePeopleInsideOptions {
  search?: string;
  page?: number;
  limit?: number;
  /** Company IDs to filter by. */
  companies?: string[];
  /** Authorization tags to filter by (employee, visit, eventual, ...). */
  authorizationTags?: string[];
  /** Transport company IDs to filter by. */
  transportCompanies?: string[];
  /** En `false` no se pide nada: sirve para no traer la vista que no se está mostrando. */
  enabled?: boolean;
}

export const usePeopleInside = ({
  search,
  page,
  limit,
  companies,
  authorizationTags,
  transportCompanies,
  enabled = true,
}: UsePeopleInsideOptions = {}) => {
  const [peopleInside, setPeopleInside] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const { authStore } = useStore();
  const userSdk = useMemo(() => new User(), []);

  const fetchPeople = useCallback(() => {
    if (!authStore.token || !enabled) return;

    setIsLoading(true);
    userSdk
      .getPeopleInside(
        authStore.token,
        moment().subtract(7, "day").unix(),
        moment().unix(),
        page,
        limit,
        search,
        undefined,
        undefined,
        companies,
        authorizationTags,
        transportCompanies
      )
      .then((res) => {
        const formattedData = (res?.data?.data || []).map((data: any) => {
          return {
            fecha: data?.event?.datetime,
            nombre: `${data?.firstname || ""} ${data?.lastname || ""}`.trim(),
            dni: data?.dni,
            empresa: getCompanyNames(
              data?.event?.company ?? data?.company,
              authStore.companies || []
            ),
            patente: data?.event?.vehicle?.plate,
            // Todas las unidades del ingreso con su estado dentro/fuera, ya resuelto por el
            // backend. Es lo mismo que lee la vista por vehículo.
            associatedVehicles: data?.associatedVehicles ?? [],
            eventId: data?.event?.id,
            userId: data?.id,
            mainEvent: data?.event?.mainEvent,
            countryCode: data?.countryCode,
            facePhoto: data?.facePhoto,
            frontDNI: data?.frontDNI,
            backDNI: data?.backDNI,
            inEventComment: data?.event?.comment,
            inEventPeopleComment: data?.event?.peopleComment,
            inEventPhoto: data?.event?.photos?.observationsPhoto,
            inEventPhoto2: data?.event?.photos?.observationsPhoto2,
            inEventPhoto3: data?.event?.photos?.observationsPhoto3,
            vehicle: data?.event?.vehicle,
            vehicleType: data?.event?.vehicleType,
            email: data?.email,
            phone: data?.phone,
            // Legajo del chofer: el egreso lo necesita para el formulario de transporte y no viene
            // en el evento de ingreso.
            transportEmployeeNumber: data?.transportEmployeeNumber,
            transportEmployeeNumbers: data?.transportEmployeeNumbers,
            // Empresa de transporte con la que entró. Viene resuelta por nombre desde
            // getPeopleInside; la autorización de acceso queda como plan B.
            transportCompanyName:
              data?.transportCompanyName ||
              data?.event?.transportCompanyName ||
              data?.event?.transportAuthorizations?.find((auth: any) => auth?.eventType === "in")
                ?.transportCompanyName ||
              "",
            host: (() => {
              const invite = (data?.event?.invites || []).find((inv: any) => inv?.host);
              if (!invite?.host) return undefined;
              return {
                firstname: invite.host.firstname,
                lastname: invite.host.lastname,
                company: invite.host.company,
              };
            })(),
            event: data?.event,
          };
        });

        setPeopleInside(formattedData);
        setTotal(res?.data?.total ?? formattedData.length);
      })
      .catch((err) => {
        console.error("Error fetching people inside:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    authStore.token,
    enabled,
    userSdk,
    authStore.companies,
    search,
    page,
    limit,
    companies,
    authorizationTags,
    transportCompanies,
  ]);

  useEffect(() => {
    fetchPeople();
  }, [fetchPeople, refetchKey]);

  const refetch = useCallback(() => {
    setRefetchKey((prev) => prev + 1);
  }, []);

  return { peopleInside, isLoading, refetch, total };
};
